import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Group, Select, Stack, Text } from '@mantine/core';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { useEditVersionContext } from '@/context/EditVersionContext/useEditVersionContext.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import useStaticVersionResources from '@/hooks/api/datasets/static/useStaticVersionResources.ts';
import { StaticDataset, StaticRevision } from '@/types/datasets.ts';
import { AutomationVersion } from '@/types/automations.ts';
import {
  get_adopt_static_revisions_url,
  get_automation_version_url,
  get_static_revisions_url,
} from '@/utils/urls.ts';
import { notifyStepMutationError } from '@/services/stepMutationErrors.ts';
import { STATIC_DATASETS_KEY } from '@/utils/queryKeys.ts';
import StaticRevisionReview, { StaticRevisionReviewSelection } from './StaticRevisionReview.tsx';

export default function StaticRevisionControls({ sources }: { sources: StaticDataset[] }) {
  const api = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();
  const { editingVersion, addToQueue, reportLatestDraftChanged, hasPendingEdits } =
    useEditVersionContext();
  const resources = useStaticVersionResources();
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [review, setReview] = useState<StaticRevisionReviewSelection | null>(null);
  const adoption = useRef<{ signature: string; requestId: string } | null>(null);
  useEffect(() => {
    setChoices({});
    setReview(null);
  }, [editingVersion?.id]);
  const required = Object.keys(resources.data?.errors ?? {});
  const complete = required.every((id) => choices[id]);
  const adopt = () => {
    if (!review || editingVersion?.id !== review.versionId || hasPendingEdits) return;
    const expectedVersion = review.versionId;
    const selections = review.selections;
    const signature = JSON.stringify([expectedVersion, Object.entries(selections).sort()]);
    if (adoption.current?.signature !== signature)
      adoption.current = { signature, requestId: crypto.randomUUID() };
    const requestId = adoption.current.requestId;
    setPending(true);
    addToQueue({
      mutationFn: async (version) => {
        if (version.id !== expectedVersion)
          throw new Error('The draft changed. Review the selection and try again.');
        const response = await api.post<{ version_id: string }>(
          get_adopt_static_revisions_url(automationId),
          {
            expected_version_id: expectedVersion,
            selections,
            request_id: requestId,
          },
        );
        return (
          await api.get<AutomationVersion>(get_automation_version_url(response.data.version_id))
        ).data;
      },
      onSuccess: () => {
        setChoices({});
        setReview(null);
        void queryClient.invalidateQueries({ queryKey: ['static-version-resources'] });
      },
      onError: (error) =>
        notifyStepMutationError('select data revisions', error, reportLatestDraftChanged),
      onComplete: () => setPending(false),
    });
  };
  return (
    <Stack gap="sm">
      <Text size="sm">Data selected for this draft</Text>
      {resources.isError && (
        <Text size="xs" c="red" role="alert">
          Draft data selections could not be loaded.
        </Text>
      )}
      {resources.data?.resources
        .filter((resource) => !sources.some((source) => source.id === resource.source_id))
        .map((resource) => (
          <Text key={resource.source_id} size="xs">
            {resource.name}: selected {resource.revision_id} · retained selection
          </Text>
        ))}
      {required
        .filter((id) => !sources.some((source) => source.id === id))
        .map((id) => (
          <Text key={id} size="xs" c="red">
            Source {id} is unavailable. Remove its input or restore the source before selecting
            data.
          </Text>
        ))}
      {sources.map((source) => (
        <RevisionChoice
          key={`${editingVersion?.id}:${source.id}`}
          source={source}
          selected={resources.data?.selections?.[source.id]}
          referenced={required.includes(source.id) || !!resources.data?.selections?.[source.id]}
          value={choices[source.id] ?? null}
          onChange={(value) =>
            setChoices((current) => {
              const next = { ...current };
              if (value) next[source.id] = value;
              else delete next[source.id];
              return next;
            })
          }
        />
      ))}
      {Object.keys(choices).length > 0 && (
        <>
          {!complete && (
            <Text size="xs">Choose a revision for every unresolved source before saving.</Text>
          )}
          <StyledButton
            variant="outline"
            onClick={() => {
              if (!editingVersion || !resources.data) return;
              setReview({
                versionId: editingVersion.id,
                before: { ...resources.data.selections },
                selections: { ...resources.data.selections, ...choices },
                names: Object.fromEntries(
                  sources.map((source) => [source.id, source.dataset.name]),
                ),
              });
            }}
            loading={pending}
            disabled={!complete || hasPendingEdits || resources.isError}
          >
            Review selected revisions
          </StyledButton>
        </>
      )}
      {review && (
        <StaticRevisionReview
          automationId={automationId}
          selection={review}
          pending={pending}
          disabled={hasPendingEdits || editingVersion?.id !== review.versionId || resources.isError}
          onClose={() => {
            if (!pending) setReview(null);
          }}
          onConfirm={adopt}
        />
      )}
    </Stack>
  );
}

function RevisionChoice({
  source,
  selected,
  referenced,
  value,
  onChange,
}: {
  source: StaticDataset;
  selected?: string;
  referenced: boolean;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const api = useApiClient();
  const automationId = useAutomationId();
  const { reportLatestDraftChanged } = useEditVersionContext();
  const queryClient = useQueryClient();
  const [observing, setObserving] = useState(false);
  const revisions = useInfiniteQuery({
    queryKey: ['static-revisions', source.id, 'newest'],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (
        await api.get<{ results: StaticRevision[]; next_after: number | null }>(
          get_static_revisions_url(source.id),
          { params: { after: pageParam, limit: 100, newest_first: true } },
        )
      ).data,
    getNextPageParam: (page) => page.next_after ?? undefined,
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.results.some((revision) =>
          ['pending', 'processing', 'sealing'].includes(revision.status),
        ),
      )
        ? 3000
        : false,
  });
  const rows = revisions.data?.pages.flatMap((page) => page.results);
  const available = rows?.filter((revision) => revision.status === 'ready') ?? [];
  const processing = rows?.some((revision) =>
    ['pending', 'processing', 'sealing'].includes(revision.status),
  );
  const captureRequest = useRef<{ datasetId: string; requestId: string } | null>(null);
  const observe = async () => {
    setObserving(true);
    try {
      if (captureRequest.current?.datasetId !== source.dataset.id)
        captureRequest.current = { datasetId: source.dataset.id, requestId: crypto.randomUUID() };
      await api.post(`${get_static_revisions_url(source.id)}/observe`, {
        expected_dataset_id: source.dataset.id,
        request_id: captureRequest.current.requestId,
      });
      captureRequest.current = null;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['static-revisions', source.id] }),
        queryClient.invalidateQueries({ queryKey: STATIC_DATASETS_KEY(automationId) }),
      ]);
    } catch (error) {
      notifyStepMutationError('capture current data', error, reportLatestDraftChanged);
    } finally {
      setObserving(false);
    }
  };
  return (
    <Stack gap={4}>
      <Text size="xs">
        {source.dataset.name}:{' '}
        {selected
          ? `selected ${selected}`
          : referenced
            ? 'selection required'
            : 'not used by this draft'}
      </Text>
      {revisions.isError && (
        <Text size="xs" c="red" role="alert">
          Data revisions could not be loaded.
        </Text>
      )}
      {processing && <Text size="xs">Processing uploaded revision…</Text>}
      {rows
        ?.filter((revision) => revision.status === 'failed')
        .map((revision) => (
          <Text key={revision.id} size="xs" c="red">
            Revision failed: {revision.failure_code || 'processing failed'}
          </Text>
        ))}
      {revisions.hasNextPage && (
        <StyledButton
          variant="outline"
          loading={revisions.isFetchingNextPage}
          onClick={() => void revisions.fetchNextPage()}
        >
          Load more revisions
        </StyledButton>
      )}
      {referenced && available.length > 0 && (
        <Select
          aria-label={`Revision for ${source.dataset.name}`}
          placeholder="Choose a revision"
          data={available.map((revision) => ({
            value: revision.id,
            label: `${new Date(revision.created_at).toLocaleString()} · ${revision.id}${revision.id === selected ? ' (selected)' : ''}`,
          }))}
          value={value}
          onChange={onChange}
        />
      )}
      {rows &&
        !revisions.isError &&
        available.length === 0 &&
        !processing &&
        !revisions.hasNextPage && (
          <Group>
            <Text size="xs">Capture the current data as a new revision before selecting it.</Text>
            <StyledButton variant="outline" onClick={() => void observe()} loading={observing}>
              Capture current data
            </StyledButton>
          </Group>
        )}
    </Stack>
  );
}
